import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	Component,
	Input,
	ElementRef,
	AfterViewInit,
	ViewEncapsulation,
	inject,
	output,
	viewChild,
	Signal,
	OutputEmitterRef,
	DestroyRef
} from '@angular/core';
import {
	DateRange,
	DefaultMatCalendarRangeStrategy,
	MAT_DATE_RANGE_SELECTION_STRATEGY,
	MatCalendar,
	MatCalendarCellCssClasses,
	MatCalendarView
} from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS, NativeDateAdapter } from '@angular/material/core';
import { MAT_MOMENT_DATE_FORMATS } from '@angular/material-moment-adapter';
import { fromEvent, Observable, merge, BehaviorSubject, throttleTime } from 'rxjs';
import { map, debounceTime, filter } from 'rxjs/operators';
import { Languages } from '@core';
import { TranslateService } from '@ngx-translate/core';
import { addDays, isSameDay } from 'date-fns';
import { CalendarView } from '@app/shared/enums/general.enums';

const CUSTOM_DATE_FORMATS = {
	...MAT_MOMENT_DATE_FORMATS,
	display: {
		...MAT_MOMENT_DATE_FORMATS.display,
		monthYearLabel: { month: 'short', year: 'numeric' }
	}
};

const moveEventThrottleTime: number = 8; // 120 fps
const startEndEventsDebounceTime: number = 50;

@Component({
	selector: 'range-calendar',
	templateUrl: './range-calendar.component.html',
	styleUrls: ['./range-calendar.component.scss'],
	providers: [
		MatCalendar,
		{ provide: DateAdapter, useClass: NativeDateAdapter, deps: [MAT_DATE_LOCALE] },
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
		{ provide: MAT_DATE_RANGE_SELECTION_STRATEGY, useClass: DefaultMatCalendarRangeStrategy }
	],
	encapsulation: ViewEncapsulation.None,
	standalone: false
})
export class RangeCalendarComponent implements AfterViewInit {
	private readonly destroyRef: DestroyRef = inject(DestroyRef);

	private elRef = inject(ElementRef);
	private calendarAdapter = inject<DateAdapter<Date>>(DateAdapter);
	private translateService = inject(TranslateService);

	readonly calendar: Signal<MatCalendar<any>> = viewChild(MatCalendar);
	@Input() set startAt(start: Date) {
		this._startAt = new Date(start);
		this.selectedDates = [start];
	}
	get startAt(): Date {
		return this._startAt;
	}
	readonly selectedDatesValueChange: OutputEmitterRef<Date[]> = output<Date[]>();

	private selectedDates: Date[] = [];
	private selectedRangeValue: DateRange<Date>;
	private isCtrlPressed: boolean = false;
	private _startAt: Date;
	private isMonthView$: BehaviorSubject<boolean> = new BehaviorSubject(true);

	public hoverRangeValue: DateRange<Date>;

	constructor() {
		this.translateService.currentLang === Languages.HE
			? this.calendarAdapter.setLocale(Languages.HE)
			: this.calendarAdapter.setLocale(Languages.EN);
	}

	ngAfterViewInit(): void {
		this.listenToRangeSelectionEvents();
		this.listenToCtrlPressEvents();
	}

	private listenToRangeSelectionEvents(): void {
		const calendarElement: HTMLElement = this.elRef.nativeElement.querySelector('mat-calendar');
		if (!calendarElement) {
			throw Error();
		}
		const start$: Observable<Date> = this.createDateSelectionObservables(
			calendarElement,
			'mousedown',
			'touchstart',
			startEndEventsDebounceTime
		);
		const move$: Observable<Date> = this.createDateSelectionObservables(
			calendarElement,
			'mousemove',
			null,
			0,
			moveEventThrottleTime
		);
		const end$: Observable<Date> = this.createDateSelectionObservables(
			calendarElement,
			'mouseup',
			'touchend',
			startEndEventsDebounceTime
		);

		let isSelectingRange: boolean = false;
		start$.subscribe((dateStart: Date): void => {
			if (dateStart) {
				isSelectingRange = true;
				if (!this.isCtrlPressed) {
					this.selectedDates = [];
				}
				this.selectedRangeValue = new DateRange<Date>(dateStart, null);
				this.emitSelectedDatesChange();
			}
		});
		move$.pipe(filter(() => isSelectingRange)).subscribe((currentDate: Date): void => {
			const startDate: Date = this.selectedRangeValue?.start;
			if (currentDate && startDate && currentDate !== startDate) {
				this.handleRangeHover(startDate, currentDate);
				this.refreshCalendar(false);
			}
		});
		end$.subscribe((dateEnd: Date): void => {
			isSelectingRange = false;
			this.hoverRangeValue = null;
			const start: Date = this.selectedRangeValue?.start;
			const end: Date = dateEnd;
			if (this.isCalendarNavigationEvent()) {
				this.refreshCalendar(false);
			}
			if (!start && !end) {
				return this.emitSelectedDatesChange();
			}
			if (start && end && !isSameDay(start, end)) {
				this.handleRangeSelection(start, end);
			} else {
				this.handleSingleSelection(start || end);
			}
			this.emitSelectedDatesChange();
		});
	}

	private isCalendarNavigationEvent(): boolean {
		return !!this.elRef.nativeElement.querySelector('range-calendar') || !this.isMonthView$.value;
	}

	private listenToCtrlPressEvents(): void {
		const ctrlPress$: Observable<boolean> = merge(
			this.createCtrlPressObservables('keydown'),
			this.createCtrlPressObservables('keyup')
		);
		ctrlPress$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ctrlPress: boolean): void => {
			this.isCtrlPressed = ctrlPress;
		});
	}

	private createCtrlPressObservables(keyEvent: string): Observable<boolean> {
		return fromEvent<KeyboardEvent>(document, keyEvent).pipe(map((event: KeyboardEvent): boolean => event.ctrlKey));
	}

	private createDateSelectionObservables(
		element: HTMLElement,
		mouseEvent: string,
		touchEvent?: string,
		debounce: number = 0,
		throttle: number = 0
	): Observable<Date> {
		return merge(
			fromEvent<MouseEvent>(element, mouseEvent, { passive: true }),
			touchEvent ? fromEvent<TouchEvent>(element, touchEvent, { passive: true }) : []
		).pipe(
			throttleTime(throttle),
			debounceTime(debounce),
			map((event: MouseEvent | TouchEvent): Date => this.getDateFromEvent(event)),
			takeUntilDestroyed(this.destroyRef)
		);
	}

	private getDateFromEvent(event: MouseEvent | TouchEvent): Date {
		let element: HTMLElement;
		if (event instanceof MouseEvent) {
			element = event.target as HTMLElement;
		} else if (event instanceof TouchEvent) {
			const touch: Touch = event.touches[0] || event.changedTouches[0];
			element = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement;
		}
		return this.getDateFromElement(element);
	}

	private getDateFromElement(element: HTMLElement): Date {
		const year: number = this.calendar().activeDate.getFullYear();
		const month: number = this.calendar().activeDate.getMonth();
		if (element.classList.contains('mat-calendar-body-cell-content')) {
			const day: number = parseInt(element.innerText);
			return new Date(year, month, day);
		}
		return null;
	}

	private convertRangeToSingleDates(): void {
		if (this.selectedRangeValue) {
			const datesFromRange: Date[] = [];
			let date: Date = this.selectedRangeValue.start;
			while (date <= this.selectedRangeValue.end) {
				datesFromRange.push(new Date(date));
				date = addDays(date, 1);
			}
			this.selectedDates = [...this.selectedDates, ...datesFromRange];
			this.selectedRangeValue = null;
		}
	}

	private emitSelectedDatesChange(): void {
		this.selectedDatesValueChange.emit(this.selectedDates);
	}

	private isDateInSelection(date: Date, datesSelection: Date[]): boolean {
		return datesSelection.some((selectedDate: Date): boolean => {
			return isSameDay(date, selectedDate);
		});
	}

	private addOrRemoveDateFromSelection(date: Date, datesSelection: Date[]): Date[] {
		return this.isDateInSelection(date, datesSelection)
			? datesSelection.filter((selectedDate: Date): boolean => !isSameDay(date, selectedDate))
			: [...datesSelection, date];
	}

	private handleRangeHover(startDate: Date, endDate: Date): void {
		if (startDate < endDate) {
			this.hoverRangeValue = new DateRange<Date>(startDate, endDate);
		} else {
			this.hoverRangeValue = new DateRange<Date>(endDate, startDate);
		}
	}
	private handleRangeSelection(start: Date, end: Date): void {
		if (start < end) {
			this.selectedRangeValue = new DateRange<Date>(start, end);
		} else {
			this.selectedRangeValue = new DateRange<Date>(end, start);
		}
		this.convertRangeToSingleDates();
		this.refreshCalendar();
	}

	private handleSingleSelection(date: Date): void {
		this.selectedDates = this.isCtrlPressed ? this.addOrRemoveDateFromSelection(date, this.selectedDates) : [date];
		this.refreshCalendar();
	}

	/**
	 * This method is used to ensure that all selected dates
	 * belong to the currently active month in the calendar
	 */
	private normalizeSelectedDates(): void {
		const currentMonth: number = this.calendar().activeDate.getMonth();
		this.selectedDates = this.selectedDates.filter((date: Date): boolean => date.getMonth() === currentMonth);
	}

	private refreshCalendar(normalizeDates = true): void {
		if (normalizeDates) {
			this.normalizeSelectedDates();
		}
		this.calendar().updateTodaysDate();
	}

	public onViewChanged(view: MatCalendarView): void {
		this.isMonthView$.next(view === CalendarView.Month);
	}

	public getDateClass = (date: Date): MatCalendarCellCssClasses => {
		const hasSelectedDates: boolean = date && this.selectedDates.length > 0;
		if (this.isMonthView$.value && hasSelectedDates && this.isDateInSelection(date, this.selectedDates)) {
			return 'custom-calendar-body-selected';
		}
		return '';
	};
}
